<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { getDataURL } from '@shared/fondof-fn/image/thumbhash'
import type { Product } from '@shared/shop-api/product/types'

type Props = {
  product: Product
  loading?: 'lazy' | 'eager'
  fetchpriority?: 'high' | 'low' | 'auto' | undefined
  useThumbhash?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: 'lazy',
  fetchpriority: undefined,
  useThumbhash: false,
})

const ioTransform = '?width=450&height=450'

const thumbnail = computed(() =>
  props.product.media?.default?.url.concat(ioTransform)
)

const thumbhash = computed(() =>
  getDataURL(props.product.media?.default?.thumbhash)
)

const imageLoaded = ref(false)

const handleImageLoad = () => {
  imageLoaded.value = true
}

const img = ref()
// native @load event can fire before Vue hydration completes
// checking img state onMounted, we make sure the loading state is updated
onMounted(() => {
  if (img.value?.complete) {
    handleImageLoad()
  }
})
</script>

<template>
  <picture data-chromatic="ignore">
    <source
      :srcset="`${thumbnail} 1x,${thumbnail} 2x`"
      media="(max-width: 768px)"
    />
    <img
      ref="img"
      :class="[
        'relative w-full transition-opacity',
        { 'opacity-0': !imageLoaded && props.useThumbhash },
        { 'opacity-50': !product.available && imageLoaded },
      ]"
      :src="thumbnail"
      :alt="props.product.attributes.style"
      :loading="props.loading"
      :fetchpriority="props.fetchpriority"
      @load="handleImageLoad"
    />
    <img
      v-if="thumbhash && props.useThumbhash"
      :class="[
        'absolute inset-0 h-full w-full scale-75 object-cover blur-md transition-opacity',
        { 'opacity-0': imageLoaded },
      ]"
      :src="thumbhash"
      :alt="props.product.attributes.style"
      aria-hidden="true"
    />
  </picture>
</template>
